const { API, ENDPOINTS, CACHE } = require("./secureConfig");
const storage = require("./secureStorage");

// In-memory cache for hot keys (non-persistent)
const memoryCache = new Map(); // chatId -> { keyBase64, expiresAt }
const userMasterKeyCache = new Map(); // userId -> { keyBase64, expiresAt }

function getAuthHeaders() {
  try {
    const token =
      typeof localStorage !== "undefined"
        ? localStorage.getItem("loginDetails")
        : null;
    return token ? { token } : {};
  } catch (_e) {
    return {};
  }
}

async function fetchJson(url, options = {}) {
  const headers = {
    "Content-Type": "application/json",
    ...getAuthHeaders(),
    ...(options.headers || {}),
  };
  const res = await fetch(url, { ...options, headers });
  if (!res.ok) {
    const text = await res.text().catch(() => "");
    throw new Error(`HTTP ${res.status}: ${text || res.statusText}`);
  }
  return await res.json();
}

function getFromMemory(chatId) {
  const entry = memoryCache.get(chatId);
  if (!entry) return null;
  if (Date.now() > entry.expiresAt) {
    memoryCache.delete(chatId);
    return null;
  }
  return entry.keyBase64 || null;
}

function putInMemory(chatId, keyBase64) {
  const ttl = CACHE.inMemoryMaxAge || 0;
  memoryCache.set(chatId, {
    keyBase64,
    expiresAt: ttl ? Date.now() + ttl : Number.MAX_SAFE_INTEGER,
  });
}

function getUserMasterKeyFromMemory() {
  const entry = userMasterKeyCache.get("userMasterKey");
  if (!entry) return null;
  if (Date.now() > entry.expiresAt) {
    userMasterKeyCache.delete("userMasterKey");
    return null;
  }
  return entry.keyBase64 || null;
}

function putUserMasterKeyInMemory(keyBase64) {
  const ttl = CACHE.inMemoryMaxAge || 0;
  userMasterKeyCache.set("userMasterKey", {
    keyBase64,
    expiresAt: ttl ? Date.now() + ttl : Number.MAX_SAFE_INTEGER,
  });
}

async function getSessionKey(chatId) {
  if (!chatId) throw new Error("getSessionKey: chatId is required");

  // 1) in-memory
  const mem = getFromMemory(chatId);
  if (mem) return mem;

  // 2) IndexedDB
  const persisted = await storage.getSessionKey(chatId);
  if (persisted) {
    putInMemory(chatId, persisted);
    return persisted;
  }

  // 3) Backend API
  const url = `${API.secureMessageBase}${ENDPOINTS.getDecryptionKey(chatId)}`;
  const json = await fetchJson(url, { method: "GET" });
  const keyBase64 = json?.data?.decryptionKey;
  if (!keyBase64)
    throw new Error("getSessionKey: backend did not return decryptionKey");

  // persist + memoize
  await storage.setSessionKey(chatId, keyBase64);
  putInMemory(chatId, keyBase64);
  return keyBase64;
}

async function setSessionKey(chatId, keyBase64) {
  if (!chatId || !keyBase64)
    throw new Error("setSessionKey: chatId and keyBase64 are required");
  await storage.setSessionKey(chatId, keyBase64);
  putInMemory(chatId, keyBase64);
  return true;
}

async function getUserMasterKey() {
  // 1) Check in-memory cache
  const mem = getUserMasterKeyFromMemory();
  if (mem) return mem;

  // 2) Check IndexedDB
  const persisted = await storage.getUserMasterKey();
  if (persisted) {
    putUserMasterKeyInMemory(persisted);
    return persisted;
  }

  // 3) Call backend API
  const url = `${API.secureMessageBase}${ENDPOINTS.getUserMasterKey}`;
  const json = await fetchJson(url, { method: "GET" });
  const keyBase64 = json?.data?.userMasterKey;
  if (!keyBase64)
    throw new Error("getUserMasterKey: backend did not return userMasterKey");

  // persist + memoize
  await storage.setUserMasterKey(keyBase64);
  putUserMasterKeyInMemory(keyBase64);
  return keyBase64;
}

async function setUserMasterKey(keyBase64) {
  if (!keyBase64) throw new Error("setUserMasterKey: keyBase64 is required");
  await storage.setUserMasterKey(keyBase64);
  putUserMasterKeyInMemory(keyBase64);
  return true;
}

async function clearAllKeys() {
  memoryCache.clear();
  userMasterKeyCache.clear();
  await storage.clearAllKeys();
}

module.exports = {
  getSessionKey,
  setSessionKey,
  getUserMasterKey,
  setUserMasterKey,
  clearAllKeys,
};
